import type { ParseJSONObject } from '@ikegps/core-api';
import { Deferred } from './deferred';
import { IkeDelayedTask } from './delayed.task';
import { IkeRestObject } from './objects/base';
import { IkeRestQuery } from './query';
import type { IkeRestDepartment } from './rest.department';

export class IkeRestBatchControl {
	boundDepartment: IkeRestDepartment;
	batches: { [key: string]: IkeRestBatch<any> };
	constructor(boundDepartment: IkeRestDepartment) {
		this.boundDepartment = boundDepartment;
		this.batches = {};
	}

	batch<T extends ParseJSONObject, K extends keyof T>(obj: IkeRestObject<T>, key: K, value: T[K]): Promise<T> {
		const batchKey = `${obj.actionName}:${key as string}`;

		let batch = this.batches[batchKey];
		if (batch == null || batch.executed) {
			this.batches[batchKey] = batch = new IkeRestBatch(this.boundDepartment.query(obj), key);
		}

		return batch.add(value as any);
	}
}

export class IkeRestBatch<T extends ParseJSONObject> {
	static BATCH_SIZE = 1000;
	static BATCH_DELAY = 5;

	key: keyof T;
	values: { [key: string]: Deferred<T> };
	query: IkeRestQuery<T>;

	executed: boolean;

	runner = new IkeDelayedTask(() => this.execute(), IkeRestBatch.BATCH_DELAY);

	constructor(query: IkeRestQuery<T>, key: keyof T) {
		this.query = query;
		this.key = key;
		this.values = {};
	}

	add(value: string): Promise<T> {
		let existing = this.values[value];
		if (existing == null) {
			if (this.executed) {
				throw new Error('Cannot add to a executed batch');
			}
			existing = this.values[value] = Deferred.create<T>();
		}
		this.runner.delay();
		return existing.promise;
	}

	async execute(): Promise<void> {
		this.executed = true;
		const toFetch = Object.keys(this.values);

		var queryBatch = [];
		do {
			queryBatch.push(toFetch.splice(0, IkeRestBatch.BATCH_SIZE));
		} while (toFetch.length > 0);

		for (const batch of queryBatch) {
			this.query.in(this.key, batch);
			this.query.limit(batch.length);

			try {
				const documents = await this.query.find();
				this.resolveDocuments(batch, documents);
			} catch (e) {
				batch.map((id) => this.values[id].reject(e));
			}
		}
	}

	resolveDocuments(batch: string[], documents: T[]): void {
		const docMap: { [key: string]: boolean } = {};

		for (const document of documents) {
			const key = document[this.key];
			const deffered = this.values[key as any];
			docMap[key as any] = true;
			if (deffered != null) {
				deffered.resolve(document);
			}
		}

		if (batch.length === documents.length) {
			return;
		}

		// Resolve null for invalid records
		for (const key of batch) {
			if (docMap[key]) {
				continue;
			}
			const deffered = this.values[key];
			deffered.resolve(null);
		}
	}
}
